import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { useImages } from '../context/ImageContext';
import './LandingPageTest.css';

const PHONE_TEL = 'tel:1-877-579-9862';

const LandingPageTest: React.FC = () => {
  const { getImage } = useImages();

  const heroImage = getImage('lp-hero-hug.jpg');
  const worryImage = getImage('lp-worry-garden.jpg');
  const quoteImage = getImage('lp-quote-couple.jpg');
  const bottomImage = getImage('lp-bottom-cta.jpg');

  return (
    <div className="lpt">
      {/* 1. Hero — navy left / image right */}
      <section className="lpt-hero">
        <div className="lpt-hero-copy">
          <h1>
            You Can&apos;t Always Be There.{' '}
            <em>Alert-1 Can.</em>
          </h1>
          <p>
            Protecting over 300,000 customers since 1988, our emergency response, automatic
            fall detection and 24/7 monitoring services help them stay independent at home.
          </p>
          <ul className="lpt-checklist lpt-checklist--light">
            <li>NO ACTIVATION FEES</li>
            <li>30-DAY FREE TRIAL</li>
            <li>FREE EQUIPMENT &amp; SHIPPING</li>
          </ul>
          <div className="lpt-btn-row">
            <Link to="/pricing" className="lpt-btn lpt-btn--red">
              PROTECT MY PARENT
            </Link>
            <a href={PHONE_TEL} className="lpt-btn lpt-btn--ghost">
              CALL TO ORDER
            </a>
          </div>
        </div>
        <div className="lpt-hero-media" role="img" aria-label="Adult child hugging aging parent">
          {heroImage ? (
            <img src={heroImage} alt="Adult child hugging aging parent" />
          ) : null}
        </div>
      </section>

      {/* 2. Worry section — white, image left / copy right */}
      <section className="lpt-worry">
        <div className="lpt-container lpt-worry-grid">
          <div className="lpt-worry-media">
            {worryImage ? (
              <img src={worryImage} alt="Senior woman tending flowers in a garden" />
            ) : null}
          </div>
          <div className="lpt-worry-copy">
            <p className="lpt-eyebrow lpt-eyebrow--red">WHY ALERT-1?</p>
            <h2>
              The Worry Never Really <em>Goes Away</em>
            </h2>
            <p>
              You check in. You call. You wonder if today is the day something happens and
              nobody is there. Alert-1 gives aging parents a simple way to get help — and
              gives adult children real peace of mind.
            </p>
            <p className="lpt-emphasis">
              Alert-1 helps ensure they&apos;re never truly alone, even when you can&apos;t be
              there yourself.
            </p>
          </div>
        </div>
      </section>

      {/* 3. Stats bar — red */}
      <section className="lpt-stats">
        <div className="lpt-container lpt-stats-grid">
          <div className="lpt-stat">
            <div className="lpt-stat-value">1 IN 3</div>
            <div className="lpt-stat-label">Seniors Experience Falls Each Year</div>
          </div>
          <div className="lpt-stat">
            <div className="lpt-stat-value">24/7</div>
            <div className="lpt-stat-label">Emergency Monitoring</div>
          </div>
          <div className="lpt-stat">
            <div className="lpt-stat-value">20–45 SEC</div>
            <div className="lpt-stat-label">Average Response Time</div>
          </div>
          <div className="lpt-stat">
            <div className="lpt-stat-value">190+</div>
            <div className="lpt-stat-label">Languages Supported</div>
          </div>
        </div>
      </section>

      {/* 4. Protection cards */}
      <section className="lpt-protection">
        <div className="lpt-container">
          <p className="lpt-eyebrow lpt-eyebrow--red lpt-center">TOTAL SAFETY</p>
          <h2 className="lpt-center">
            Protection That Works Even When <em>You Can&apos;t</em>
          </h2>
          <div className="lpt-cards-3">
            <article className="lpt-feature-card">
              <div className="lpt-feature-icon" aria-hidden>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </div>
              <h3>AUTOMATIC FALL DETECTION</h3>
              <p>Help can be summoned even if they can&apos;t press the button.</p>
            </article>
            <article className="lpt-feature-card">
              <div className="lpt-feature-icon" aria-hidden>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </div>
              <h3>EMERGENCY RESPONSE</h3>
              <p>Trained operators answer every call and coordinate the right help fast.</p>
            </article>
            <article className="lpt-feature-card">
              <div className="lpt-feature-icon" aria-hidden>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3>FAMILY NOTIFICATIONS</h3>
              <p>Loved ones can be notified so the whole family stays in the loop.</p>
            </article>
          </div>
        </div>
      </section>

      {/* 5. Full-bleed quote / image band */}
      <section className="lpt-quote">
        <div className="lpt-quote-bg" aria-hidden>
          {quoteImage ? <img src={quoteImage} alt="" /> : null}
        </div>
        <div className="lpt-quote-overlay">
          <div className="lpt-container lpt-quote-inner">
            <p className="lpt-eyebrow lpt-eyebrow--red">DESIGNED FOR THEM</p>
            <h2>
              Help Them Stay <em>Home Longer</em>
            </h2>
            <p>
              Most families want aging loved ones to stay independent at home. Alert-1
              makes that safer — without complicated equipment or long contracts.
            </p>
          </div>
        </div>
      </section>

      {/* 6. Pricing CTA card — red box */}
      <section className="lpt-pricing">
        <div className="lpt-container">
          <div className="lpt-pricing-card">
            <div className="lpt-pricing-left">
              <h2>
                Our Most Popular Choice For <em>Aging Parents</em>
              </h2>
              <p className="lpt-price">STARTING AT $29.95/MO.</p>
              <Link to="/pricing" className="lpt-btn lpt-btn--white">
                VIEW PLANS &amp; PRICING
              </Link>
            </div>
            <div className="lpt-pricing-right">
              <h3>HOME &amp; FALL DETECTION PLAN</h3>
              <ul className="lpt-checklist lpt-checklist--light">
                <li>24/7 MONITORING</li>
                <li>AUTOMATIC FALL DETECTION OPTION</li>
                <li>WATERPROOF HELP BUTTON</li>
                <li>FREE EQUIPMENT &amp; SHIPPING</li>
                <li>NO LONG-TERM CONTRACT</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Testimonials — navy cards */}
      <section className="lpt-testimonials">
        <div className="lpt-container">
          <h2 className="lpt-center">
            Trusted By Families <em>Across The Country</em>
          </h2>
          <div className="lpt-cards-3">
            {[
              {
                title: 'Peace of mind for our whole family',
                body: 'We got Alert-1 for Mom after her first fall. Knowing help is one button away lets all of us sleep better.',
                name: '— Customer review',
              },
              {
                title: 'Simple setup, fast response',
                body: "Setup was easy and the monitoring team has been kind and professional every time we've tested the system.",
                name: '— Customer review',
              },
              {
                title: 'Worth every penny',
                body: 'Dad lives alone and loves his independence. Alert-1 helps him keep it without us worrying constantly.',
                name: '— Customer review',
              },
            ].map((t) => (
              <article key={t.title} className="lpt-testimonial-card">
                <div className="lpt-stars" aria-label="5 stars">
                  {'★★★★★'}
                </div>
                <h3>{t.title}</h3>
                <p>{t.body}</p>
                <p className="lpt-testimonial-name">{t.name}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 8. FAQ — light blue */}
      <section className="lpt-faq">
        <div className="lpt-container">
          <h2 className="lpt-center lpt-faq-title">
            Have <em>Questions?</em>
          </h2>
          <div className="lpt-faq-grid">
            <div>
              <h3>Is Setup Difficult?</h3>
              <p>
                No. Most systems are ready in minutes. We include simple instructions and
                phone support if you need help.
              </p>
            </div>
            <div>
              <h3>Will My Parent Actually Wear It?</h3>
              <p>
                Designs are discreet and comfortable — pendants, wristbands, and options
                built for everyday life so they&apos;re more likely to keep it on.
              </p>
            </div>
            <div>
              <h3>Is There a Long-Term Contract?</h3>
              <p>
                No long-term contracts. Cancel anytime — and try risk-free with a 30-day
                trial.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. Incentives bar — red outlined boxes */}
      <section className="lpt-incentives">
        <div className="lpt-container lpt-incentives-grid">
          <div className="lpt-incentive">30-DAY TRIAL</div>
          <div className="lpt-incentive">NO CONTRACTS</div>
          <div className="lpt-incentive">FREE SHIPPING</div>
          <div className="lpt-incentive">VETERAN &amp; AARP DISCOUNTS</div>
        </div>
      </section>

      {/* 10. Bottom CTA — navy / image split */}
      <section className="lpt-hero lpt-hero--bottom">
        <div className="lpt-hero-copy">
          <h2>
            Your Parent Deserves To Live Fully.{' '}
            <em>You Deserve To Rest Easy.</em>
          </h2>
          <p className="lpt-hero-subcaps">
            FREE EQUIPMENT • FREE SHIPPING • CANCEL ANYTIME
          </p>
          <div className="lpt-btn-row">
            <Link to="/pricing" className="lpt-btn lpt-btn--red">
              PROTECT MY PARENT
            </Link>
            <a href={PHONE_TEL} className="lpt-btn lpt-btn--ghost">
              CALL TO ORDER
            </a>
          </div>
        </div>
        <div className="lpt-hero-media" role="img" aria-label="Grandfather with granddaughter">
          {bottomImage ? (
            <img src={bottomImage} alt="Grandfather with granddaughter" />
          ) : null}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPageTest;
